import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { get } from "lodash";
import GaugeChart from "react-gauge-chart";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Skeleton,
  Alert,
  Chip
} from "@mui/material";

import "../css/font-awesome-pro.css";
import { getData, getSerialData } from "../api";
import BackButton from "./Components/BackButton";

export default function DeviceDetails() {
  const navigate = useNavigate();
  const { serialNumber } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return navigate("/", { replace: true });

    const isAdmin = sessionStorage.getItem("isAdmin") === "true";
    if (isAdmin && !serialNumber) return navigate("list", { replace: true });

    const fetch = serialNumber ? getSerialData : getData;
    
    setLoading(true);
    setError(null);
    
    fetch(serialNumber)
      .then((resp) => {
        setData(resp || {});
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load device data");
        setLoading(false);
      });
  }, [navigate, serialNumber]);

  const detailsUrl = serialNumber
    ? `#home/details2/${serialNumber}`
    : "#home/details2/";

  // Calculate health status
  const getHealthStatus = (percent) => {
    if (percent < 0.5) return { label: "Good", color: "success" };
    if (percent < 0.8) return { label: "Fair", color: "warning" };
    return { label: "Replace Soon", color: "error" };
  };

  const flatCutterPercent = Math.min(
    get(data, "data.flatcut", 0) / get(data, "flatCutterHealth", 1),
    1
  );
  
  const bitCutterPercent = Math.min(
    get(data, "data.bitcut", 0) / get(data, "bitCutterHealth", 1),
    1
  );

  const flatStatus = getHealthStatus(flatCutterPercent);
  const bitStatus = getHealthStatus(bitCutterPercent);

  if (error) {
    return (
      <Box sx={{ p: 2, maxWidth: 1400, mx: "auto" }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box  sx={{ p: 0, maxWidth: 1400, mx: "auto"}}>
      {/* Header */}
      <BackButton />
      {serialNumber && (
        <Typography variant="body2" color="text.secondary">
          Serial Number:&nbsp;
          <a
            href={`/#/home/device/${serialNumber}`}
            style={{
              color: "#2563eb",
              fontWeight: 600,
              textDecoration: "none"
            }}
            onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
            onMouseOut={(e) => (e.target.style.textDecoration = "none")}
          >
            {serialNumber}
          </a>
        </Typography>
      )}


      <Grid container spacing={1.5} className="!p-2 !md:p-1">
        {/* ================= PRIMARY METRICS ================= */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
            Primary Metrics
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <KpiCard
            icon="fa-key"
            title="Total Key Count"
            value={get(data, "data.keycount", "-")}
            unit="keys"
            loading={loading}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <KpiCard
            icon="fa-clock"
            title="Total Motor Runtime"
            value={get(data, "data.runtime", "-")}
            unit="mins"
            loading={loading}
            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <KpiCard
            icon="fa-bolt"
            title="Energy Consumption"
            value={(parseInt(get(data, "data.energy", 0)) / 1000).toFixed(2)}
            unit="units"
            loading={loading}
            gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
          />
        </Grid>

        {/* ================= CUTTING OPERATIONS ================= */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={700} mt={0.5} mb={0.5}>
            Cutting Operations
          </Typography>
        </Grid>

        <Grid item xs={6} md={3}>
          <KpiCard
            icon="fa-ruler"
            title="Total Flat Cut"
            value={get(data, "data.flatcut", "-")}
            unit="cuts"
            loading={loading}
            dense
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <KpiCard
            icon="fa-minus"
            title="Single Flat"
            value={get(data, "data.singleside", "-")}
            unit="cuts"
            loading={loading}
            dense
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <KpiCard
            icon="fa-plus"
            title="Double Flat"
            value={get(data, "data.doubleside", "-")}
            unit="cuts"
            loading={loading}
            dense
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <KpiCard
            icon="fa-microchip"
            title="Total Bit Cut"
            value={get(data, "data.bitcut", "-")}
            unit="cuts"
            loading={loading}
            dense
          />
        </Grid>

        {/* ================= HEALTH MONITORING ================= */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={700} mt={0.5} mb={0.5}>
            Cutter Health Status
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <GaugeCard
            icon="fa-circle-half-stroke"
            title="Flat Cutter Health"
            percent={flatCutterPercent}
            status={flatStatus}
            loading={loading}
            currentValue={get(data, "data.flatcut", 0)}
            maxValue={get(data, "flatCutterHealth", 0)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <GaugeCard
            icon="fa-circle-half-stroke"
            title="Bit Cutter Health"
            percent={bitCutterPercent}
            status={bitStatus}
            loading={loading}
            currentValue={get(data, "data.bitcut", 0)}
            maxValue={get(data, "bitCutterHealth", 0)}
          />
        </Grid>

       <Grid  item xs={12} md={4}>
        <Card 
          sx={{
            height: "100%",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
              backgroundColor: "transparent",
              boxShadow: "none"
          }}
        >
          {/* Empty / content area */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Footer */}
          <Box

            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "center",
              backgroundColor: "#fff",
              borderRadius: "10px 10px 8px 8px",
            }}
          >
            <Button
              href={detailsUrl}
              variant="contained"
              size="medium"
              sx={{
                px: 4,
                py: 1.2,
                borderRadius: 3,
                fontWeight: 700,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 6px 16px rgba(102,126,234,0.4)",
                "&:hover": {
                  boxShadow: "0 10px 22px rgba(102,126,234,0.5)",
                  transform: "translateY(-1px)"
                }
              }}
            >
              View Detailed Analytics
            </Button>
          </Box>
        </Card>
      </Grid>

      </Grid>
    </Box>
  );
}

/* ================= REUSABLE COMPONENTS ================= */

function KpiCard({ 
  title, 
  value, 
  unit, 
  icon, 
  dense = false, 
  loading = false, 
  gradient 
}) {
  // Responsive sizing based on dense prop
  const iconSize = dense ? 40 : 48;
  const iconFontSize = dense ? "16px" : "20px";
  // const titleFontSize = dense ? "0.65rem" : "0.7rem";
  const valueVariant = dense ? "h6" : "h5";
  const cardHeight = dense ? 70 : 80;

  // Loading state
  if (loading) {
    return (
      <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Skeleton variant="rectangular" height={cardHeight} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        height: "100%", 
        borderRadius: 2,
        boxShadow: 1,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)"
        }
      }}
    >
      <CardContent 
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 2
        }}
      >
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center"
          sx={{ width: "100%" }}
        >
          {/* Icon Box */}
          <Box
            sx={{
              width: iconSize,
              height: iconSize,
              borderRadius: 1.5,
              background: gradient || "linear-gradient(135deg, #000000 0%, #10031d 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: iconFontSize,
              color: "white",
              boxShadow: 1,
              flexShrink: 0
            }}
          >
            <i className={`fa-solid ${icon}`} />
          </Box>

          {/* Content Box */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
          <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{
            textTransform: "uppercase",
            fontSize: {
              xs: "0.85rem",
              sm: "0.9rem",
              md: "1rem"
            },
            color: "text.primary",
            mb: 0.5
          }}
        >
          {title}
        </Typography>


            {/* Value and Unit */}
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography
                variant={valueVariant}
                fontWeight={800}
                color="text.primary"
                sx={{ lineHeight: 1 }}
              >
                {value}
              </Typography>
              {unit && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  {unit}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function GaugeCard({ title, percent, icon, status, loading = false, currentValue, maxValue }) {
  if (loading) {
    return (
      <Card sx={{ height: "100%", borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Skeleton variant="rectangular"  />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        height: "100%", 
        borderRadius: 2,
        boxShadow: 1,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)"
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <Box
            sx={{
              width: 32,
              height: "100%",
              borderRadius: 1.5,
              bgcolor: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px"
            }}
          >
            <i className={`fa-solid ${icon}`} />
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {title}
            </Typography>
          </Box>

          <Chip 
            label={status.label} 
            color={status.color} 
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Box sx={{ }}>
          <GaugeChart
            id={title.replace(/\s+/g, "-").toLowerCase()}
            colors={["#10b981", "#fbbf24", "#ef4444"]}
            nrOfLevels={12}
            arcWidth={0.3}
            percent={percent}
            hideText
            animate
            animDelay={0}
          />
        </Box>

        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            <strong>{currentValue.toLocaleString()}</strong> / {maxValue.toLocaleString()} cycles
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem" }}>
            {((1 - percent) * 100).toFixed(1)}% remaining
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}